import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ReactTable from 'react-table';
import { ChevronLeft } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { find, includes, cloneDeep, get, intersection, without } from 'lodash';

import { Snackbar } from '../../../../common/components/Snackbar/Snackbar';
import { Toolbar } from '../../../../common/components/Toolbar/Toolbar';
import Button from '../../../../common/components/Button/Button';

import { RoleForm } from '../RoleForm';
import { PermissionStatusToggle } from '../PermissionStatusToggle';
import { fetchPermissions, createRole, editRole, hideRoleSnackbar } from '../../actions';

import './_styles.scss';

const emptyRole = {
  name: '',
  permissions: [],
  status: 'ACTIVE',
};

const toolBarStyle = {
  gridColumn: '1 / 3',
};

const rolesEqual = (role1, role2) =>
  role1.name === role2.name &&
  role1.status === role2.status &&
  role1.permissions.length === role2.permissions.length &&
  intersection(role1.permissions, role2.permissions).length === role1.permissions.length;

const CREATE_MODE = 'create';
const EDIT_MODE = 'edit';

function RoleEditorComponent({
  mode,
  role: propRole,
  createRole,
  editRole,
  hideSnackbar,
  hasError,
  snackbarMessage,
  fetchPermissions,
  isSubmitting,
  isPermissionsFetching,
  permissions,
  user,
}) {
  const [initial, setInitial] = useState(true);
  const [isPristine, setIsPristine] = useState(true);
  const [role, setRole] = useState(cloneDeep(emptyRole));
  const navigate = useNavigate();

  useEffect(() => {
    if (initial) {
      setInitial(false);

      if (mode === EDIT_MODE) {
        setRole({
          name: propRole.name,
          permissions: [...propRole.permissions],
          status: propRole.status,
        });
      }

      return;
    }

    setIsPristine(rolesEqual(propRole || emptyRole, role));
  }, [role, propRole, mode]);

  useEffect(() => {
    fetchPermissions(user.currentPermissions);
  }, []);

  const getTitle = () => {
    if (mode === EDIT_MODE) {
      return propRole.name;
    }

    return 'Create Access Level';
  };

  const onBackToList = () => {
    navigate(-1);
  };

  const onPermissionToggle = (permission, checked) => {
    let permissions = [];

    if (checked) {
      permissions = [...role.permissions, permission];
    } else {
      permissions = without(role.permissions, permission);
    }

    setRole({
      ...role,
      permissions,
    });
  };

  const getColumns = () => [
    {
      Header: 'Permission Name',
      id: 'name',
      accessor: 'label',
    },
    {
      accessor: 'status',
      minWidth: 60,
      sortable: false,
      Cell: ({ original }) => (
        <PermissionStatusToggle name={original.value} toggled={includes(role.permissions, original.value)} onToggle={onPermissionToggle} />
      ),
    },
  ];

  const onSubmit = ({ name }) => {
    setRole({
      ...role,
      name,
    });

    if (mode === CREATE_MODE) {
      createRole(role, navigate);
    } else if (mode === EDIT_MODE) {
      editRole(role);
    }
  };

  const onCancel = () => {
    setRole(mode === EDIT_MODE ? cloneDeep(propRole) : cloneDeep(emptyRole));
  };

  const onChange = (name, value) => {
    setRole({
      ...role,
      [name]: value,
    });
  };

  const handleCloseSnackbar = () => {
    hideSnackbar();
  };

  const renderSnackbar = () => {
    const bodyStyle = {
      backgroundColor: 'rgba(0, 0, 0, 0.87)',
    };

    if (hasError) {
      bodyStyle.backgroundColor = 'rgba(204, 0, 0, 0.87)';
    }

    return <Snackbar open={!!snackbarMessage} message={snackbarMessage || ''} autoHideDuration={2000} onClose={handleCloseSnackbar} />;
  };

  return (
    <div className="role-editor">
      <Toolbar style={toolBarStyle}>
        <Tooltip title="Back To List">
          <Button variant="contained" className="back-button" onClick={onBackToList}>
            <ChevronLeft />
          </Button>
        </Tooltip>
        <span className="toolbar-separator" />
        <div className="role-editor-title">{getTitle()}</div>
      </Toolbar>
      <div className="role-editor-form">
        <RoleForm
          initialValues={{ name: get(propRole, 'name', '') }}
          isPristine={isPristine}
          isSubmitting={isSubmitting}
          onSubmit={onSubmit}
          onCancel={onCancel}
          onFormChange={onChange}
        />
      </div>
      <div className="role-editor-permissions">
        <div className="permissions-panel">
          <ReactTable
            className="-highlight"
            columns={getColumns()}
            data={permissions}
            loading={isPermissionsFetching}
            showPagination={false}
            minRows={0}
          />
        </div>
      </div>
      {renderSnackbar()}
    </div>
  );
}

RoleEditorComponent.propTypes = {
  mode: PropTypes.oneOf([CREATE_MODE, EDIT_MODE]),
  role: PropTypes.object,
  navigate: PropTypes.func,
  isPermissionsFetching: PropTypes.bool,
  hasError: PropTypes.bool,
  snackbarMessage: PropTypes.string,
  permissions: PropTypes.array,
  fetchPermissions: PropTypes.func,
  createRole: PropTypes.func,
  editRole: PropTypes.func,
  hideSnackbar: PropTypes.func,
  isSubmitting: PropTypes.bool,
};

RoleEditorComponent.defaultProps = {
  isPermissionsFetching: false,
};

const mapStateToProps = (state, { roleId }) => ({
  user: state.login.user,
  role: find(state.roles.roles, { uuid: roleId }),
  permissions: state.roles.permissions,
  isPermissionsFetching: state.roles.isPermissionsFetching,
  isSubmitting: state.roles.isRoleUpserting,
  snackbarMessage: state.roles.snackbarMessage,
  hasError: state.roles.hasError,
});

const mapDispatchToProps = (dispatch, { roleId }) => ({
  fetchPermissions: (permissions) => dispatch(fetchPermissions(permissions)),
  createRole: (role, navigate) => dispatch(createRole(role, navigate)),
  editRole: (role) => dispatch(editRole(roleId, role)),
  hideSnackbar: () => dispatch(hideRoleSnackbar()),
});

export const RoleEditor = connect(mapStateToProps, mapDispatchToProps)(RoleEditorComponent);
